import { useCallback, useContext } from "react";

import {
  TenantContext,
  chosenIdentityProviderKey,
  useCreateAccountMutation,
  useIdentityProvidersQuery,
  useInvitationCheck,
} from "@nens/ranalib";

import { useRanaSearchState } from "@/hooks/router/rana-search-state";

import { InvitationFormValues } from "../utils/validate-account-form";

export function useInvitationAcceptPage(tenant: string, invitationId: string | undefined) {
  const { user } = useContext(TenantContext);

  const [pageType, setPageType] = useRanaSearchState<"welcome" | "sign-up" | "sign-in">(
    "page",
    "welcome",
    ["string"]
  );

  const {
    data: invitation,
    error,
    isError,
    isLoading,
    isSuccess,
  } = useInvitationCheck(tenant, invitationId);

  const {
    mutate: createAccountMutate,
    isMutating: accountLoading,
    isSuccess: accountSuccess,
    isError: accountError,
    errors: accountErrors,
  } = useCreateAccountMutation(tenant);

  const identityProvidersResponse = useIdentityProvidersQuery(tenant);

  const identityProvidersLoaded = identityProvidersResponse.isSuccess;
  const identityProviders = identityProvidersResponse.data?.items ?? [];

  const redirectToLogin = useCallback(
    (identityProvider?: string) => {
      // Logout by deleting the identity provider choice
      const identityProviderKey = chosenIdentityProviderKey(tenant);
      window.localStorage.removeItem(identityProviderKey);
      // Don't navigate internally, use a real page change so state is flushed
      window.location.assign(
        identityProvider ? `/${tenant}?provider=${identityProvider}` : `/${tenant}`
      );
    },
    [tenant]
  );

  const createAccount = useCallback(
    (values: InvitationFormValues) => {
      createAccountMutate({
        username: values.username,
        email: "" + invitation?.email,
        invitationId: "" + invitation?.id,
        givenName: "" + values.given_name,
        familyName: "" + values.family_name,
        password: "" + values.password,
      });
    },
    [invitation, createAccountMutate]
  );

  // Check if the logged in user is the same as the invited user (case insensitive)
  const isLoggedIn = user && user.email.toLowerCase() === invitation?.email.toLowerCase();

  return {
    isLoggedIn,
    invitation,
    error,
    isError,
    isSuccess,
    isLoading,
    identityProvidersLoaded,
    identityProviders,
    redirectToLogin,
    pageType,
    setPageType,
    createAccount,
    accountLoading,
    accountSuccess,
    accountError,
    accountErrors,
  };
}
